import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieGrid from '@/components/feature/MovieGrid';
import Pagination from '@/components/feature/Pagination';
import { filterMovies, getHotMovies, movies } from '@/lib/movieData';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { regionOptions, yearRangeOptions, type MediaType } from '@/types/movie';

const PAGE_SIZE = 12;

const typeTabs: { key: MediaType | 'all'; label: string }[] = [
  { key: 'all', label: '全部类型' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'variety', label: '综艺' },
];

function parseList(value: string | null): string[] {
  return value ? value.split(',').filter(Boolean) : [];
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const type = (searchParams.get('type') as MediaType | 'all') || 'all';
  const regionSelected = parseList(searchParams.get('region'));
  const yearSelected = parseList(searchParams.get('year'));
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  const [keyword, setKeyword] = useState(q);
  const hot = getHotMovies(6);

  useDocumentMeta(
    q ? `“${q}” 搜索结果 - 久久热` : '站内搜索 - 久久热影视内容平台',
    q
      ? `久久热站内搜索“${q}”的结果，支持按类型、地区、年份筛选影视内容。`
      : '在久久热搜索电影、电视剧、综艺、导演与演员，快速找到想看的影视作品。'
  );

  const update = (patch: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    });
    if (resetPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const toggle = (key: 'region' | 'year', value: string) => {
    const current = parseList(searchParams.get(key));
    const nextList = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    update({ [key]: nextList.join(',') });
  };

  const years = useMemo(
    () => yearSelected
      .map((label) => yearRangeOptions.find((y) => y.label === label))
      .filter((y): y is { label: string; min: number; max: number } => Boolean(y)),
    [yearSelected]
  );

  const results = useMemo(
    () => filterMovies({ keyword: q, type, regions: regionSelected, years, sort: 'hot' }),
    [q, type, regionSelected, years]
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    update({ q: keyword.trim() || null });
  };

  const chip = (active: boolean) =>
    `h-8 px-3.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`;

  const hasFilter = regionSelected.length > 0 || yearSelected.length > 0 || type !== 'all';

  return (
    <div className="w-full">
      {/* Header */}
      <section className="w-full px-4 md:px-8 pt-8 md:pt-10 pb-8 bg-background-100 border-b border-background-200">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />
        <h1 className="mt-4 font-display text-2xl md:text-3xl font-bold text-foreground-950">站内搜索</h1>
        <p className="mt-2 text-sm text-foreground-500">输入关键词，检索久久热全站影视内容。</p>

        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="flex items-center gap-2 h-12 px-4 rounded-md border border-background-300 bg-background-50 flex-1">
            <i className="ri-search-line text-foreground-500" />
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、剧集、导演、演员"
              className="flex-1 bg-transparent text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
            />
            {keyword && (
              <button type="button" aria-label="清空" onClick={() => setKeyword('')} className="text-foreground-400 hover:text-foreground-700 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            )}
          </div>
          <button type="submit" className="h-12 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            搜索
          </button>
        </form>
      </section>

      {/* Filters */}
      <section className="w-full px-4 md:px-8 py-5 border-b border-background-200">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-500 w-16">类型</span>
            {typeTabs.map((t) => (
              <button key={t.key} type="button" onClick={() => update({ type: t.key === 'all' ? null : t.key })} className={chip(type === t.key)}>{t.label}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-500 w-16">地区</span>
            {regionOptions.map((r) => (
              <button key={r} type="button" onClick={() => toggle('region', r)} className={chip(regionSelected.includes(r))}>{r}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-500 w-16">年份</span>
            {yearRangeOptions.map((y) => (
              <button key={y.label} type="button" onClick={() => toggle('year', y.label)} className={chip(yearSelected.includes(y.label))}>{y.label}</button>
            ))}
          </div>
          {hasFilter && (
            <button type="button" onClick={() => update({ type: null, region: null, year: null })} className="self-start inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-background-300 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line" />重置筛选
            </button>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="mb-5 text-sm text-foreground-600">
          {q ? (
            <>关键词 <span className="font-semibold text-primary-600">“{q}”</span> 共找到 <span className="font-semibold text-primary-600">{results.length}</span> 个结果</>
          ) : (
            <>为你展示站内共 <span className="font-semibold text-primary-600">{movies.length}</span> 部影视内容</>
          )}
          {totalPages > 1 && <span className="ml-2 text-foreground-400">· 第 {safePage} / {totalPages} 页</span>}
        </div>

        {pageItems.length > 0 ? (
          <>
            <MovieGrid movies={pageItems} columns={4} />
            <Pagination page={safePage} totalPages={totalPages} onChange={(p) => update({ page: String(p) }, false)} />
          </>
        ) : (
          <div className="py-14 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
            <span className="inline-flex w-14 h-14 rounded-full bg-background-200 items-center justify-center">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400" />
            </span>
            <h3 className="mt-4 font-display text-lg font-bold text-foreground-900">没有找到相关内容</h3>
            <p className="mt-1.5 text-sm text-foreground-500">换个关键词试试，或者看看下面这些热门内容。</p>
            <div className="mt-5 flex justify-center gap-3 flex-wrap">
              <button type="button" onClick={() => { setKeyword(''); update({ q: null, type: null, region: null, year: null }); }} className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-refresh-line" />清空条件
              </button>
              <Link to="/" className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-home-4-line" />返回首页
              </Link>
            </div>

            <div className="mt-10 max-w-4xl mx-auto text-left">
              <h4 className="font-display text-base font-bold text-foreground-950">
                <a href="#hot-search" className="hover:text-primary-600 transition-colors cursor-pointer">热门推荐</a>
              </h4>
              <div className="mt-4">
                <MovieGrid movies={hot} columns={4} />
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}